import * as React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';

import { useAuth } from '@restworld/feature-authentication';
import { useOrganizationsService } from '@restworld/data-services';
import { useBotChallenge, useGmapsScript } from '@restworld/utils-common';
import {
  orgOnboardingExistingUserFlowAtom,
  orgOnboardingReferralInfoAtom,
  orgOnboardingReferralPrefillAtom,
  orgOnboardingPartnerInfoAtom,
  orgOnboardingRestaurantDataAtom,
  orgOnboardingJPDataAtom,
  orgOnboardingSocialContactPrefillAtom,
  OrgOnboardingReferralInfo,
} from './use-organization-onboarding';
import { InvalidReferralCode } from './invalid-referral-code';
import { useProspectUpsert } from './use-prospect-tracking';
// A static import on purpose (RW-235, item 1): the onboarding used to come from
// `dynamic(..., { ssr: false })`, a chunk the browser only requested after auth and the
// lookups had settled, followed by a second lazy chunk for step 1. It still renders on the
// client only (it reads localStorage on its first render), but its code now arrives with the
// page, so taking over from the server step 1 costs no extra round trip.
import OrganizationOnboarding from './onboarding';
import { ServerStepOne } from './server-step-one';
import { isServerStepOneHeld } from './server-step-one-hold';
import { isDeferredBillingCampaign } from './deferred-billing';
import { pickTryForFreeVariant, tryForFreeAssignmentAtom } from './try-for-free-variant';
import {
  googleAddresInputValueAtom,
  skipVenueSearchAppearAnimationAtom,
  venueSearchInitialValueTypedAtom,
} from '../restaurants/restaurant-onboarding-modal/google-address-input-result';

export interface GettingStartedOgTags {
  title: string;
  description: string;
  image: string;
  canonicalUrl: string;
}

export interface GettingStartedPageProps {
  og: GettingStartedOgTags;
}

export const GettingStartedPage: React.FunctionComponent<GettingStartedPageProps> = ({ og }) => {
  const router = useRouter();
  const { isAuthenticated, isInitialized } = useAuth();
  // Prospects, OTP, AI ad and organization creation below all need a Turnstile token: load the script now.
  useBotChallenge();
  const isExistingUserFlow = useAtomValue(orgOnboardingExistingUserFlowAtom);
  const setExistingUserFlow = useSetAtom(orgOnboardingExistingUserFlowAtom);
  const setReferralInfo = useSetAtom(orgOnboardingReferralInfoAtom);
  const setReferralPrefill = useSetAtom(orgOnboardingReferralPrefillAtom);
  const setPartnerInfo = useSetAtom(orgOnboardingPartnerInfoAtom);
  const setTryForFreeAssignment = useSetAtom(tryForFreeAssignmentAtom);
  const restaurantData = useAtomValue(orgOnboardingRestaurantDataAtom);
  const jpData = useAtomValue(orgOnboardingJPDataAtom);
  const setSocialContactPrefill = useSetAtom(orgOnboardingSocialContactPrefillAtom);
  const { upsertProspect } = useProspectUpsert();
  const orgService = useOrganizationsService();
  const setGoogleInputValue = useSetAtom(googleAddresInputValueAtom);
  const setSkipVenueSearchAppear = useSetAtom(skipVenueSearchAppearAnimationAtom);
  const setVenueSearchInitialValueTyped = useSetAtom(venueSearchInitialValueTypedAtom);

  // The search field of the server step 1. Whatever the visitor types there, before or after
  // hydration, goes into the atom the real field starts from, so the swap keeps it. The flag
  // next to it tells the real field the value was typed, so it searches it at once: the field
  // treats a value it mounts with as a saved one otherwise, and saved values never fetch.
  const handleServerStepOneInputChange = React.useCallback(
    (value: string) => {
      setGoogleInputValue(value);
      setVenueSearchInitialValueTyped(true);
    },
    [setGoogleInputValue, setVenueSearchInitialValueTyped]
  );
  const serverStepOneInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    // Text typed before React attached its handlers lives only in the DOM.
    const typedBeforeHydration = serverStepOneInputRef.current?.value;
    if (typedBeforeHydration) handleServerStepOneInputChange(typedBeforeHydration);
    // The visitor already sees the field: the real one must not fade in again on the swap.
    if (!isServerStepOneHeld()) setSkipVenueSearchAppear(true);
    // The flag belongs to this page. Leaving before the real field took over (the atoms outlive
    // the route) must not make the next venue search, say the dashboard's "add venue" modal,
    // fetch that leftover text as if it had just been typed there.
    return () => setVenueSearchInitialValueTyped(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Google Maps script is started by the page HTML (`pages/_document.tsx`), in parallel
  // with the bundle. This call only adopts that tag, so a dev build without the key, or a
  // route reached client-side, still loads Maps; the field asks for the same URL and never
  // adds a second one.
  useGmapsScript({ libraries: 'places' });

  const [isReferralLoading, setIsReferralLoading] = React.useState(true);
  const [isInvalidReferralCode, setIsInvalidReferralCode] = React.useState(false);
  const [isPartnerLoading, setIsPartnerLoading] = React.useState(true);

  // Track whether user was authenticated when page first loaded.
  // This prevents redirecting users who become authenticated during the onboarding flow.
  const wasAuthenticatedOnMount = React.useRef<boolean | null>(null);

  React.useEffect(() => {
    if (isInitialized && wasAuthenticatedOnMount.current === null) {
      wasAuthenticatedOnMount.current = isAuthenticated;
    }
  }, [isAuthenticated, isInitialized]);

  // One-shot mount tasks: restore referral info saved across a Start-Fresh
  // reload. Step reconciliation lives in useOrgOnboardingUrlSync
  // (see onboarding.tsx) to avoid a mount-order race with that hook.
  const didMountTasksRef = React.useRef(false);
  React.useEffect(() => {
    if (didMountTasksRef.current) return;
    didMountTasksRef.current = true;
    if (isExistingUserFlow) return;

    try {
      const stashed = sessionStorage.getItem('org-onboarding-referral-info-restore');
      if (stashed) {
        const parsed = JSON.parse(stashed) as OrgOnboardingReferralInfo;
        setReferralInfo(parsed);
        sessionStorage.removeItem('org-onboarding-referral-info-restore');
      }
    } catch {
      // Ignore parse/storage errors — the ?ref= URL param flow will recover.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A new Google user who picked "I'm looking for staff" is redirected here with their verified
  // email/name in the URL. Persist those as contact prefill, then strip the params so they don't
  // linger in the URL (shared/bookmarked links, prospect tracking).
  //
  // google_id is deliberately NOT consumed: it would arrive unverified in the URL and the backend
  // casts it as-is, so we drop it (stripped below with the other params) and let the next verified
  // Google login stamp it by email.
  const didConsumeSocialParamsRef = React.useRef(false);
  React.useEffect(() => {
    if (!router.isReady || didConsumeSocialParamsRef.current) return;

    const { email, name, surname, google_id, ...restQuery } = router.query;
    if (!email && !name && !surname && !google_id) return;
    didConsumeSocialParamsRef.current = true;

    if (typeof email === 'string' || typeof name === 'string' || typeof surname === 'string') {
      // Google gives one display name; if there's no explicit surname param, split
      // it on the first space into name/surname.
      const fullName = typeof name === 'string' ? name.trim() : '';
      const [firstName, ...rest] = fullName ? fullName.split(/\s+/) : [];
      const contactName = firstName || '';
      const contactSurname = typeof surname === 'string' && surname ? surname : rest.join(' ');
      const contactEmail = typeof email === 'string' ? email : '';

      setSocialContactPrefill({ name: contactName, surname: contactSurname, email: contactEmail });

      // Capture the Google lead in prospects right away, so an abandon before the
      // first onboarding step still leaves a contactable lead. Reads the Google
      // profile here (not the contactDetails progress atom), so it doesn't trip the
      // resume modal.
      upsertProspect({
        last_completed_step: 'social_signup',
        contact_email: contactEmail || undefined,
        contact_name: contactName || undefined,
        contact_surname: contactSurname || undefined,
      });
    }

    router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  // Referral codes don't apply to authenticated users — `/getting-started`
  // is a sign-up page. Strip `?ref=` and any cached referral info up-front
  // before any downstream logic reads them.
  React.useEffect(() => {
    if (!router.isReady || !isInitialized) return;
    if (wasAuthenticatedOnMount.current !== true) return;

    if (router.query.ref) {
      const { ref, ...restQuery } = router.query;
      router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    }
    setReferralInfo(null);
  }, [router.isReady, router, isInitialized, setReferralInfo]);

  // Whether the user is legitimately returning from the Flow 3 login redirect:
  // a logged-in user fills the onboarding form, hits an email/phone collision
  // with no phone-on-file at the last step, gets sent to /login, comes back
  // here to auto-create the entities under their existing account.
  //
  // A true "Flow 3 return" must have both atoms populated. If the flag is set
  // but the data isn't there, it's a stale localStorage value from an
  // abandoned past flow — drop it and redirect.
  const isFlow3Return = isExistingUserFlow && !!restaurantData && !!jpData;

  React.useEffect(() => {
    if (isInitialized && wasAuthenticatedOnMount.current === true && !isFlow3Return) {
      if (isExistingUserFlow) setExistingUserFlow(false);
      router.replace('/employer/homepage');
    }
  }, [isInitialized, router, isFlow3Return, isExistingUserFlow, setExistingUserFlow]);

  // Resolve referral code from ?ref= URL param AFTER auth check.
  React.useEffect(() => {
    if (!router.isReady || !isInitialized) return;

    // Skip referral lookup for authenticated users. They'll either be
    // redirected by the effect above, or they're a Flow 3 return — either
    // way the `?ref=` was stripped upfront and no referral context applies.
    if (wasAuthenticatedOnMount.current === true) {
      setIsReferralLoading(false);
      return;
    }

    const refCode = router.query.ref as string | undefined;
    if (!refCode) {
      setIsReferralLoading(false);
      return;
    }

    orgService
      .lookupReferralCode(refCode)
      .then((response) => {
        const data = response.data;
        const info: OrgOnboardingReferralInfo = {
          referral_code: data.referral_code,
          referrer_restaurant_name: data.referrer_restaurant_name,
          referrer_organization_id: data.referrer_organization_id,
          credits_invitee: data.credits_invitee,
        };
        setReferralInfo(info);

        // The venue this prospect named when they asked for the invite, so the first step
        // opens with the search box already filled. A referrer with more than one open
        // request gets nothing back, which is correct — guessing between them would put one
        // prospect's venue in front of another.
        //
        // Returned into the chain on purpose, so the loading gate below waits for it. The
        // Google autocomplete copies its starting value into internal state once, on mount
        // (`google-auto-complete-input.tsx`), and the onboarding only mounts once this flag
        // clears. Letting this resolve afterwards would fill the box with text that never
        // opens a dropdown — worse than not filling it at all.
        //
        // The inner catch is load-bearing: without it a failure here would reject into the
        // outer handler and wrongly mark a perfectly good referral code invalid.
        return orgService
          .getReferralOnboardingPrefill({ referral_code: data.referral_code })
          .then(({ data: prefill }) => {
            if (prefill?.venue) setReferralPrefill({ venue: prefill.venue });
          })
          .catch(() => {
            // A missing hint is not a failure worth surfacing — the user simply searches.
          });
      })
      .catch(() => {
        setReferralInfo(null);
        setIsInvalidReferralCode(true);
      })
      .finally(() => {
        setIsReferralLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.ref, isInitialized]);

  // Resolve the partner code from ?partner= URL param AFTER auth check. The code
  // can be a B2B ambassador or a static acquisition campaign. Mirrors the ?ref=
  // flow but is purely cosmetic: the intro screen needs the name and free days.
  // The partner_code itself is captured by useUTMSource and forwarded to the
  // create-org payload independently, so a failed lookup (an unknown code) still
  // keeps the code as plain attribution and simply shows no intro screen: no
  // "invalid code" branch.
  React.useEffect(() => {
    if (!router.isReady || !isInitialized) return;

    if (wasAuthenticatedOnMount.current === true) {
      setPartnerInfo(null);
      setIsPartnerLoading(false);
      return;
    }

    const partnerCode = router.query.partner as string | undefined;
    if (!partnerCode) {
      setIsPartnerLoading(false);
      return;
    }

    orgService
      .lookupPartnerCode(partnerCode)
      .then((response) => {
        const partnerInfo = {
          partner_code: response.data.partner_code,
          source_type: response.data.source_type,
          free_days: response.data.free_days,
          partner_name: response.data.partner_name,
          campaign_name: response.data.campaign_name,
          deferred_billing: response.data.deferred_billing === true,
        };
        setPartnerInfo(partnerInfo);
        // Try for Free A/B test (RW-411): split once, on the first visit with the code; a
        // visitor who already has a variant keeps it.
        if (isDeferredBillingCampaign(partnerInfo)) {
          setTryForFreeAssignment(
            (current) => current ?? { variant: pickTryForFreeVariant(), partner_code: partnerInfo.partner_code }
          );
        }
      })
      .catch(() => {
        setPartnerInfo(null);
      })
      .finally(() => {
        setIsPartnerLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.partner, isInitialized]);

  const handleProceedWithout = React.useCallback(() => {
    setIsInvalidReferralCode(false);
    setReferralInfo(null);
    const { ref, ...restQuery } = router.query;
    router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
  }, [router, setReferralInfo]);

  const ogHead = (
    <Head>
      <title>{og.title}</title>
      {/* key="canonical" replaces the default tag from <CanonicalUrl /> in _app instead of adding a second one. */}
      <link rel="canonical" href={og.canonicalUrl} key="canonical" />
      <meta name="description" content={og.description} />
      <meta property="og:title" content={og.title} />
      <meta property="og:description" content={og.description} />
      <meta property="og:image" content={og.image} />
      <meta property="og:url" content={og.canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={og.title} />
      <meta name="twitter:description" content={og.description} />
      <meta name="twitter:image" content={og.image} />
      {/* The venue search needs the Maps script (googleapis) and its modules (gstatic). */}
      <link rel="preconnect" href="https://maps.googleapis.com" key="preconnect-maps-api" />
      <link rel="preconnect" href="https://maps.gstatic.com" key="preconnect-maps-static" />
    </Head>
  );

  if (wasAuthenticatedOnMount.current === true && !isFlow3Return) {
    return ogHead;
  }

  // Auth, router and lookups not settled yet. This is also what the server renders and what the
  // first client render must match: step 1 drawn without reading the browser. The inline script
  // inside it hides it before the first paint for a visitor with ?ref= or ?partner=, a stored
  // session or saved progress, who see the same blank screen as before until their flow resolves.
  if (isReferralLoading || isPartnerLoading) {
    return (
      <>
        {ogHead}
        <ServerStepOne inputRef={serverStepOneInputRef} onInputChange={handleServerStepOneInputChange} />
      </>
    );
  }

  if (isInvalidReferralCode) {
    return (
      <>
        {ogHead}
        <InvalidReferralCode
          onGoBack={() => router.push('/ask-for-referral')}
          onProceedWithout={handleProceedWithout}
        />
      </>
    );
  }

  return (
    <>
      {ogHead}
      <OrganizationOnboarding />
    </>
  );
};
